import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  Alert,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import StatusBadge from 'src/components/TransferOrder/StatusBadge';
import Loader from 'src/components/Loader';
import { TransferOrder, TransferOrderStatus, TransferOrderRecord } from 'src/types/TransferOrder.types';
import { TransferOrderService } from 'src/services/transfer-order-service';
import { toast } from 'react-toastify';

const ShipOrder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [currentOrder, setCurrentOrder] = useState<TransferOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [notes, setNotes] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const fetchOrder = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const order = await TransferOrderService.getById(id);
      setCurrentOrder(order);
    } catch (error: any) {
      console.error('Failed to fetch transfer order:', error);
      const errorMessage = error?.message || 'Failed to load transfer order';
      dispatch(showAlert({ message: errorMessage, type: 'error' }));
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const handleShip = async () => {
    if (!currentOrder) return;

    setOperationLoading(true);
    try {
      const shippingNotes = [
        carrier && `Carrier: ${carrier}`,
        trackingNumber && `Tracking: ${trackingNumber}`,
        notes,
      ]
        .filter(Boolean)
        .join(' | ');

      const result = await TransferOrderService.ship({
        transferOrderId: currentOrder.Id,
        notes: shippingNotes,
        trackingNumber,
        carrier,
      });

      dispatch(
        showAlert({
          message: result.message || 'Order shipped successfully',
          type: 'success',
        })
      );
      toast.success(result.message || 'Order shipped successfully');

      setShowConfirmDialog(false);
      navigate(`/transfer-orders/${id}`);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to ship order';
      dispatch(showAlert({ message: errorMessage, type: 'error' }));
      toast.error(errorMessage);
    } finally {
      setOperationLoading(false);
    }
  };

  const pickedItems = currentOrder?.Records?.filter((r) => r.pickedQuantity > 0) || [];
  const totalPickedQty = pickedItems.reduce((sum, r) => sum + r.pickedQuantity, 0);
  const isFullyPicked =
    currentOrder?.Records?.every((r) => r.pickedQuantity === r.allocatedQuantity) || false;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Loader />
      </Box>
    );
  }

  if (!currentOrder) {
    return (
      <Box>
        <Alert severity="error">Transfer order not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/transfer-order')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  if (
    currentOrder.Status !== TransferOrderStatus.Ready &&
    currentOrder.Status !== TransferOrderStatus.Picking
  ) {
    return (
      <Box>
        <Alert severity="warning">
          This order cannot be shipped. Current status:{' '}
          <StatusBadge status={currentOrder.Status} type="order" />
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/transfer-orders/${id}`)} sx={{ mt: 2 }}>
          Back to Order Details
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate(`/transfer-orders/${id}`)}>
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h4" fontWeight={700}>
            Ship Order
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentOrder.OrderName || `Order #${currentOrder.Id.slice(0, 8)}`} - {currentOrder.ClientName}
          </Typography>
        </Box>
        <StatusBadge status={currentOrder.Status} type="order" size="medium" />
      </Stack>

      {/* Warning if not fully picked */}
      {!isFullyPicked && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          This order is not fully picked. Shipping now will only ship the items that have been picked.
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Items to Ship
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {pickedItems.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Quantity
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {totalPickedQty}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Client
              </Typography>
              <Typography variant="h6" fontWeight={600} noWrap>
                {currentOrder.ClientName}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {isFullyPicked ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <WarningIcon color="warning" />
                )}
                {isFullyPicked ? ' Ready' : ' Partial'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Items to Ship */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Items to Ship
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Barcode</TableCell>
                <TableCell>Source Location</TableCell>
                <TableCell align="center">Picked Quantity</TableCell>
                <TableCell>Box Ref</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pickedItems.length > 0 ? (
                pickedItems.map((item: TransferOrderRecord, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                        {item.barcode}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.sourceStorageUnitName || '-'}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={700}>
                        {item.pickedQuantity}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.boxRefNumber || '-'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No items picked yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Shipping Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Shipping Details (Optional)
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Carrier"
            placeholder="e.g., DHL, FedEx, UPS"
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
          />
          <TextField
            fullWidth
            label="Tracking Number"
            placeholder="Enter tracking number"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
          />
          <TextField
            fullWidth
            label="Shipping Notes"
            placeholder="Add any shipping notes..."
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </Paper>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={() => navigate(`/transfer-orders/${id}`)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<LocalShippingIcon />}
          onClick={() => setShowConfirmDialog(true)}
          disabled={operationLoading || pickedItems.length === 0}
          size="large"
        >
          Ship Order
        </Button>
      </Stack>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocalShippingIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Confirm Shipment
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity={isFullyPicked ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {isFullyPicked
              ? 'All items have been picked and are ready to ship.'
              : 'Only partially picked items will be shipped. Remaining items can be shipped later.'}
          </Alert>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to ship this order?
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Client:</strong> {currentOrder.ClientName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Items:</strong> {pickedItems.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Total Quantity:</strong> {totalPickedQty}
            </Typography>
            {carrier && (
              <Typography variant="body2" color="text.secondary">
                <strong>Carrier:</strong> {carrier}
              </Typography>
            )}
            {trackingNumber && (
              <Typography variant="body2" color="text.secondary">
                <strong>Tracking:</strong> {trackingNumber}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleShip} disabled={operationLoading}>
            {operationLoading ? 'Shipping...' : 'Confirm Ship'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShipOrder;
